/**
 * ScopeBadges component - displays scope indicators for processing rules.
 */

import { CarrierOption, CustomerOption, ProcessingRule } from "@/types/rules";
import { Tooltip } from "@mui/material";

interface ScopeBadgesProps {
  rule: ProcessingRule;
  carrier?: CarrierOption | null;
  customer?: CustomerOption | null;
}

/**
 * Compute scope badges from rule's filter fields.
 * Returns "Global" if all filters are null, otherwise individual badges for each set filter.
 */
export function ScopeBadges({ rule, carrier, customer }: ScopeBadgesProps) {
  const hasAnyScope =
    rule.document_type || rule.source_type || rule.carrier_code || rule.customer_ref_code;

  if (!hasAnyScope) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        Global
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {rule.document_type && (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          Doc: {rule.document_type}
        </span>
      )}

      {rule.source_type && (
        <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
          Source: {rule.source_type}
        </span>
      )}

      {rule.carrier_code && (
        <Tooltip
          title={carrier ? `${carrier.name} (${carrier.ref_carrier_code})` : rule.carrier_code}
          arrow
        >
          <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
            Carrier: {rule.carrier_code}
          </span>
        </Tooltip>
      )}

      {rule.customer_ref_code && (
        <Tooltip
          title={
            customer
              ? `${customer.name} — ${customer.ref_client_code} — ${customer.address}, ${customer.city}, ${customer.state} ${customer.zip_code}`
              : rule.customer_ref_code
          }
          arrow
        >
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
            Customer: {rule.customer_ref_code}
          </span>
        </Tooltip>
      )}
    </div>
  );
}
